// WeatherService.js - Tomorrow.io weather API integration
// Provides real-time weather data for airports with caching and rate limiting

import { debug } from '../lib/debug.js';

const TOMORROW_API_KEY = import.meta.env.VITE_TOMORROW_API_KEY;
const CACHE_DURATION_MS = 60 * 60 * 1000; // 60 minutes - weather doesn't change that fast

class WeatherService {
  constructor() {
    this.cache = new Map(); // key: ICAO, value: { data, timestamp }
    this.callsToday = 0;
    this.lastResetDate = new Date().toDateString();
    this.rateLimitedUntil = 0; // Timestamp when we can make requests again
  }

  checkRateLimit() {
    // If we got a 429, back off for 5 minutes
    if (Date.now() < this.rateLimitedUntil) {
      return false;
    }

    const today = new Date().toDateString();
    if (today !== this.lastResetDate) {
      this.callsToday = 0;
      this.lastResetDate = today;
    }
    return this.callsToday < 450; // Leave buffer (500 limit)
  }

  // MEMORY LEAK FIX: Prune stale cache entries (older than 60 minutes)
  pruneCache() {
    const now = Date.now();
    let pruned = 0;
    for (const [icao, cached] of this.cache.entries()) {
      if (now - cached.timestamp >= CACHE_DURATION_MS) {
        this.cache.delete(icao);
        pruned++;
      }
    }
    if (pruned > 0) {
      debug(`[WeatherService] Pruned ${pruned} stale cache entries`);
    }
  }

  async getAirportWeather(icao, lat, lon) {
    // MEMORY LEAK FIX: Prune stale entries on each access
    this.pruneCache();

    // Check cache first
    const cached = this.cache.get(icao);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      return cached.data;
    }

    // Check rate limit
    if (!this.checkRateLimit()) {
      console.warn('[WeatherService] Rate limit approaching, using cached data');
      return cached?.data || null;
    }

    // Check if API key exists
    if (!TOMORROW_API_KEY) {
      console.warn('[WeatherService] No API key configured');
      return null;
    }

    try {
      // Fetch from API
      this.callsToday++;
      const url = `https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&apikey=${TOMORROW_API_KEY}`;
      const response = await fetch(url);

      if (!response.ok) {
        // If rate limited, back off for 5 minutes
        if (response.status === 429) {
          this.rateLimitedUntil = Date.now() + 5 * 60 * 1000;
          console.warn('[WeatherService] Rate limited by API, backing off for 5 minutes');
        } else {
          console.error('[WeatherService] API error:', response.status);
        }
        return cached?.data || null;
      }

      const data = await response.json();
      const weatherData = data.data?.values || null;

      // Cache it
      if (weatherData) {
        this.cache.set(icao, { data: weatherData, timestamp: Date.now() });
      }

      return weatherData;
    } catch (error) {
      console.error('[WeatherService] Fetch error:', error);
      return cached?.data || null;
    }
  }

  // Only fetch weather for airports with active/scheduled flights
  async updateActiveAirports(activeAirports) {
    const results = {};
    for (const airport of activeAirports) {
      const weather = await this.getAirportWeather(airport.icao, airport.lat, airport.lon);
      if (weather) {
        results[airport.icao] = weather;
      }
    }
    return results;
  }

  // Get cached weather without making API call
  getCachedWeather(icao) {
    const cached = this.cache.get(icao);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      return cached.data;
    }
    return null;
  }

  // Get all cached weather data
  getAllCachedWeather() {
    const result = {};
    for (const [icao, cached] of this.cache.entries()) {
      if (Date.now() - cached.timestamp < CACHE_DURATION_MS) {
        result[icao] = cached.data;
      }
    }
    return result;
  }

  // Get API usage stats
  getUsageStats() {
    return {
      callsToday: this.callsToday,
      cacheSize: this.cache.size,
      rateLimitRemaining: 450 - this.callsToday
    };
  }
}

export const weatherService = new WeatherService();
export default weatherService;
